/**
 * A custom date class that adjusts serverTimeAPI by a predefined difference between server and client serverTimeAPI.
 * The serverTimeAPI difference can be set once during the application's initialization.
 *
 * If no arguments are provided (i.e., `new ServerDate()` or `ServerDate.now()`), the current serverTimeAPI
 * will be returned adjusted by the server-client serverTimeAPI gap.
 * If arguments (e.g., timestamp, date string, or date components) are provided, the class behaves
 * like the native `Date` object and does not apply the gap.
 */
export class ServerDate extends Date {
  /**
   * A flag indicating whether the serverTimeAPI difference has already been set.
   * @private
   */
  private static hasBeenSet = false

  /**
   * The difference in milliseconds between the server and client serverTimeAPI.
   * Defaults to 0 if not set.
   * @private
   */
  private static differenceBetweenServerAndClientTimeMs = 0

  /**
   * Creates a new ServerDate instance. If no arguments are provided, the instance
   * will represent the current date and serverTimeAPI adjusted by the server-client serverTimeAPI difference.
   * If arguments are provided, the class behaves like a native `Date` object and does not apply the gap.
   *
   * @param {...ConstructorParameters<typeof Date>} args - Parameters for the Date constructor.
   * These can include a timestamp, date string, or individual date components (year, month, day, etc.).
   *
   * If no arguments are provided, the constructor uses `Date.now()` adjusted by the gap.
   */
  constructor(...args: any) {
    if (!args || !args?.length) {
      super(Date.now() + ServerDate.differenceBetweenServerAndClientTimeMs)
    } else {
      super(...(args as ConstructorParameters<typeof Date>))
    }
  }

  /**
   * Sets the serverTimeAPI difference (gap) between the server and client in milliseconds.
   * This method can only be called once during the application's lifecycle.
   *
   * The gap affects the results of `new ServerDate()` or `ServerDate.now()`.
   *
   * @param {number} gapMs - The serverTimeAPI gap in milliseconds to apply to all ServerDate instances.
   * @throws {Error} If the serverTimeAPI gap has already been set.
   */
  static setGap(gapMs: number): void {
    if (this.hasBeenSet) {
      throw new Error(
        `Time gap already set. It should be set once on init application.`
      )
    } else {
      this.differenceBetweenServerAndClientTimeMs = gapMs
      this.hasBeenSet = true
    }
  }

  /**
   * Returns the current timestamp adjusted by the server-client serverTimeAPI difference.
   * Works like the native `Date.now()` but includes the gap adjustment.
   *
   * If the gap has been set, this method returns the timestamp with the gap added.
   *
   * @returns {number} The current timestamp adjusted by the gap in milliseconds.
   */
  static now(): number {
    const currentTime = Date.now()
    return currentTime + ServerDate.differenceBetweenServerAndClientTimeMs
  }
}
